import React, { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { desktop, tablet } from 'services/ui';
import { pluralForm } from 'services/pluralForm';

import { Colors } from 'context';
import { PopupContainer } from 'molecules';
import { Button } from 'atoms';
import { PATHS } from 'router/config';
import {
  isUnpaidInvoicesModalDue,
  markUnpaidInvoicesModalShown,
  unpaidInvoicesModalOpenVar,
  useLmsAccess,
} from 'core';

/**
 * ТЗ 0074: поп-ап «у вас неоплаченные фактуры» — один раз за вход.
 *
 * Условия и флаг «уже показан» — в isUnpaidInvoicesModalDue. Флаг пишется при
 * ОТКРЫТИИ, а не при закрытии: иначе перезагрузка с открытым поп-апом показала
 * бы его снова. `me` приходит асинхронно — отсюда эффект, а не проверка при
 * монтировании.
 *
 * Пока поп-ап открыт, unpaidInvoicesModalOpenVar держит опросный поп-ап
 * (SurveyNagModal) в очереди: он откроется после закрытия этого.
 */
export const UnpaidInvoicesModal = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId, activeRole, unpaidInvoicesCount, overdueInvoicesCount, isSuspended } =
    useLmsAccess();
  const [isShowing, setIsShowing] = useState(false);

  useEffect(() => {
    if (isUnpaidInvoicesModalDue({ userId, activeRole, unpaidInvoicesCount, isSuspended })) {
      markUnpaidInvoicesModalShown(userId as string);
      unpaidInvoicesModalOpenVar(true);
      setIsShowing(true);
    }
  }, [userId, activeRole, unpaidInvoicesCount, isSuspended]);

  // Приостановка пришла, пока поп-ап открыт, — на /invoices уже красная
  // область, второе сообщение поверх неё не нужно.
  useEffect(() => {
    if (isSuspended && isShowing) {
      unpaidInvoicesModalOpenVar(false);
      setIsShowing(false);
    }
  }, [isSuspended, isShowing]);

  // Шаблон размонтируется при каждом переходе: открытый флаг не должен
  // пережить сам поп-ап и навсегда запереть опросный.
  useEffect(
    () => () => {
      unpaidInvoicesModalOpenVar(false);
    },
    [],
  );

  const onClose = useCallback(() => {
    unpaidInvoicesModalOpenVar(false);
    setIsShowing(false);
  }, []);

  const onGoToInvoices = useCallback(() => {
    onClose();
    navigate(PATHS.invoices);
  }, [navigate, onClose]);

  const hasOverdue = overdueInvoicesCount > 0;
  const count = hasOverdue ? overdueInvoicesCount : unpaidInvoicesCount;
  const form = pluralForm(count);

  return (
    <PopupContainer title={t('unpaidInvoicesModal.title')} isShowing={isShowing} onClose={onClose}>
      <Content data-testid="unpaid-invoices-modal">
        {hasOverdue ? (
          <>
            <Lead $alert>{t(`unpaidInvoicesModal.overdue.${form}`, { n: count })}</Lead>
            <Text>{t('unpaidInvoicesModal.overdueWarning')}</Text>
          </>
        ) : (
          <Lead>{t(`unpaidInvoicesModal.unpaid.${form}`, { n: count })}</Lead>
        )}
      </Content>

      <ButtonsWrapper>
        <CloseButton variant="text" onClick={onClose}>
          {t('unpaidInvoicesModal.close')}
        </CloseButton>
        <GoButton onClick={onGoToInvoices}>{t('unpaidInvoicesModal.goToInvoices')}</GoButton>
      </ButtonsWrapper>
    </PopupContainer>
  );
});

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 20px 40px;

  ${tablet} {
    padding: 32px 40px 48px;
  }

  ${desktop} {
    padding: 0 60px 48px;
  }
`;

const Lead = styled.div<{ $alert?: boolean }>`
  font-size: 18px;
  line-height: 1.35;
  font-weight: 600;
  color: ${({ $alert }) => ($alert ? Colors.signal_red : Colors.black)};
`;

const Text = styled.div`
  font-size: 16px;
  line-height: 1.4;
  color: ${Colors.black};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  background-color: ${Colors.bg_gray};
  padding: 20px 20px 32px;
  border-radius: 0 0 24px 24px;

  ${tablet} {
    flex-direction: row;
    justify-content: flex-end;
    gap: 16px;
    padding: 12px 40px;
  }

  ${desktop} {
    padding: 16px 60px;
  }
`;

const GoButton = styled(Button)`
  ${tablet} {
    max-width: max-content;
  }
`;

const CloseButton = styled(GoButton)``;
